import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { Th, Td, btn, field } from "@/components/ui-kit";

const label = (b, i) => (i === b.length - 1 ? `${b[i] || "?"}+` : `${b[i] || "?"}-${(Number(b[i + 1]) || 1) - 1}`);

const Regions = ({ regions, setRegions, first }) => {
  const set = (i, k, v) => setRegions(regions.map((g, j) => (j === i ? { ...g, [k]: v } : g)));
  return (
    <div data-testid="pricing-regions">
      <p className="mb-2 text-xs text-slate-400">Orders under {first || "?"} pallets: price per pallet = ex-works + delivery for the region. The region is found by FC code, then by postcode area.</p>
      <div className="space-y-1.5">
        {regions.length > 0 && (
          <div className="hidden gap-1.5 sm:grid sm:grid-cols-[150px_90px_1fr_2fr_40px]">
            {["Region", "£ per pallet", "Postcode areas", "FC codes"].map((h) => <span key={h} className="font-mono text-[10px] uppercase text-slate-500">{h}</span>)}<span />
          </div>
        )}
        {regions.map((g, i) => (
          <div key={i} className="grid gap-1.5 sm:grid-cols-[150px_90px_1fr_2fr_40px]" data-testid={`pricing-region-${i}`}>
            <Input value={g.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Region" className={`h-8 text-xs ${field}`} data-testid={`pricing-region-${i}-name`} />
            <Input value={g.rate} onChange={(e) => set(i, "rate", e.target.value)} placeholder="£/pallet" className={`h-8 text-xs ${field}`} data-testid={`pricing-region-${i}-rate`} />
            <Input value={g.areas} onChange={(e) => set(i, "areas", e.target.value)} placeholder="Postcode areas e.g. DE, LE" className={`h-8 text-xs ${field}`} data-testid={`pricing-region-${i}-areas`} />
            <Input value={g.fcs} onChange={(e) => set(i, "fcs", e.target.value)} placeholder="FC codes e.g. EMA1, EMA2" className={`h-8 text-xs ${field}`} data-testid={`pricing-region-${i}-fcs`} />
            <Button size="sm" variant="ghost" className={btn.danger} onClick={() => setRegions(regions.filter((_, j) => j !== i))} data-testid={`pricing-region-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="ghost" className={`mt-2 ${btn.ghost}`} onClick={() => setRegions([...regions, { name: "", rate: "", areas: "", fcs: "" }])} data-testid="pricing-add-region"><Plus className="mr-1 h-3.5 w-3.5" /> Add region</Button>
    </div>
  );
};

export const PricingDialog = ({ partner, onOpenChange }) => {
  const [breaks, setBreaks] = useState([]);
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    if (!partner) return;
    api.get(`/partners/${partner.code}/pricing`).then((r) => {
      const ex = r.data.ex_works || {};
      setBreaks(r.data.breaks);
      setRows([...new Set([...Object.keys(r.data.prices), ...Object.keys(ex)])].map((sap) => ({ sap, ex: ex[sap] ?? "", p: (r.data.prices[sap] || []).map((x) => (x == null ? "" : x)) })));
      setRegions((r.data.regions || []).map((g) => ({ ...g, areas: g.areas.join(", "), fcs: g.fcs.join(", ") })));
    });
    api.get("/items").then((r) => setItems(r.data.items || r.data));
  }, [partner]);
  const desc = (sap) => items.find((i) => i.ep_sku === sap)?.description || "not in Item Master";
  const setP = (ri, bi, v) => setRows(rows.map((r, j) => (j === ri ? { ...r, p: breaks.map((_, k) => (k === bi ? v : r.p[k] ?? "")) } : r)));
  const split = (s) => String(s || "").split(/[,\s]+/).filter(Boolean);
  const save = () => api.put(`/partners/${partner.code}/pricing`, {
    breaks: breaks.map(Number),
    prices: Object.fromEntries(rows.filter((r) => r.sap).map((r) => [r.sap, breaks.map((_, k) => r.p[k] ?? "")])),
    ex_works: Object.fromEntries(rows.filter((r) => r.sap && r.ex !== "").map((r) => [r.sap, r.ex])),
    regions: regions.map((g) => ({ ...g, areas: split(g.areas), fcs: split(g.fcs) })),
  })
    .then(() => { toast.success(`${partner.name}: price breaks saved`); onOpenChange(false); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open={!!partner} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto border-slate-800 bg-[#1F232C] text-slate-100" data-testid="pricing-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Price breaks - {partner?.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Sell price per pallet by SAP material. The break is chosen from the total pallets on the PO and the price goes into the SAP IDoc line. Breaks are the starting pallet count of each band.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="pricing-breaks">
          <span className="text-slate-400">Breaks start at (pallets):</span>
          {breaks.map((b, i) => <Input key={i} value={b} onChange={(e) => setBreaks(breaks.map((x, j) => (j === i ? e.target.value : x)))} className={`h-8 w-16 ${field}`} data-testid={`pricing-break-${i}`} />)}
          <Button size="sm" variant="ghost" className={btn.ghost} onClick={() => setBreaks([...breaks, ""])} data-testid="pricing-add-break"><Plus className="h-3.5 w-3.5" /></Button>
          {breaks.length > 1 && <Button size="sm" variant="ghost" className={btn.ghost} onClick={() => setBreaks(breaks.slice(0, -1))} data-testid="pricing-remove-break"><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-800"><tr><Th>SAP material</Th><Th>Description</Th><Th>Ex-works (under {breaks[0] || "?"})</Th>{breaks.map((_, i) => <Th key={i}>{label(breaks, i)} pallets</Th>)}<Th></Th></tr></thead>
            <tbody className="divide-y divide-slate-800/70">
              {rows.map((r, ri) => (
                <tr key={ri} data-testid={`pricing-row-${r.sap || ri}`}>
                  <Td><Input value={r.sap} onChange={(e) => setRows(rows.map((x, j) => (j === ri ? { ...x, sap: e.target.value.trim() } : x)))} className={`h-8 w-24 ${field}`} data-testid={`pricing-sap-${ri}`} /></Td>
                  <Td className="text-xs text-slate-400">{desc(r.sap)}</Td>
                  <Td><Input value={r.ex ?? ""} onChange={(e) => setRows(rows.map((x, j) => (j === ri ? { ...x, ex: e.target.value } : x)))} className={`h-8 w-24 ${field}`} data-testid={`pricing-ex-${ri}`} /></Td>
                  {breaks.map((_, bi) => <Td key={bi}><Input value={r.p[bi] ?? ""} onChange={(e) => setP(ri, bi, e.target.value)} className={`h-8 w-24 ${field}`} data-testid={`pricing-price-${ri}-${bi}`} /></Td>)}
                  <Td><Button size="sm" variant="ghost" className={btn.danger} onClick={() => setRows(rows.filter((_, j) => j !== ri))} data-testid={`pricing-remove-${ri}`}><Trash2 className="h-3.5 w-3.5" /></Button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Regions regions={regions} setRegions={setRegions} first={breaks[0]} />
        <div className="flex gap-2">
          <Button variant="ghost" className={btn.ghost} onClick={() => setRows([...rows, { sap: "", ex: "", p: [] }])} data-testid="pricing-add-row"><Plus className="mr-1.5 h-4 w-4" /> Add product</Button>
          <Button onClick={save} className={`ml-auto ${btn.primary}`} data-testid="pricing-save-btn">Save price breaks</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
